import { Link } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { Smartphone } from "lucide-react";
import LazyImage from "@/components/ui/LazyImage";

const footerLinks = {
  Platform: [
    { label: "Social Feed", href: "/feed" },
    { label: "Market Hub", href: "/marketplace" },
    { label: "Events", href: "/events" },
    { label: "Jobs", href: "/jobs" },
    { label: "Dating", href: "/dating" },
    { label: "School Zone", href: "/campus" },
    { label: "QuizVerse", href: "/quizverse" },
  ],
  Services: [
    { label: "Wallet & Escrow", href: "/wallet" },
    { label: "Forex Trading", href: "/trading" },
    { label: "Resume AI", href: "/resume-ai" },
    { label: "Logistics", href: "/logistics" },
    { label: "eSIM Store", href: "/esim-store" },
  ],
  Marketplace: [
    { label: "Skill Trades", href: "/skill-trades" },
    { label: "Freelance", href: "/freelance" },
    { label: "Contract Bids", href: "/contract-bids" },
    { label: "Legal Directory", href: "/legal-directory" },
    { label: "Business Directory", href: "/business-directory" },
    { label: "Beauty & Spa", href: "/beauty" },
    { label: "Food Court", href: "/food-court" },
    { label: "Advertise", href: "/advertise" },
  ],
  Company: [
    { label: "About Us", href: "/about" },
    { label: "Careers", href: "/careers" },
    { label: "Press", href: "/press" },
    { label: "Blog", href: "/blog" },
    { label: "Contact", href: "/contact" },
    { label: "How It Works", href: "/how-it-works" },
  ],
  Support: [
    { label: "Help Center", href: "/help" },
    { label: "Community Guidelines", href: "/community-guidelines" },
    { label: "Terms", href: "/terms" },
    { label: "Privacy & GDPR", href: "/privacy" },
    { label: "Subscription", href: "/subscription" },
  ],
};

// Categories hidden on the mobile landing footer to keep it compact.
const MOBILE_HIDDEN_CATEGORIES = ["Platform", "Services", "Marketplace"];

const Footer = () => {
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://gloogum.com";
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(siteUrl)}&bgcolor=FFFFFF&color=000000&margin=8`;

  return (
    <footer className="bg-foreground text-background py-16">
      <div className="container px-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
          <div className="col-span-2 md:col-span-1">
            <h3 className="font-display text-2xl font-bold gradient-text mb-3">Gloogum</h3>
            <p className="text-background/80 text-sm mb-4">The everything platform for everyone.</p>
            {/* Mobile QR Code */}
            <div className="hidden md:block bg-background/10 rounded-xl p-3 border border-background/10 inline-block">
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="w-4 h-4 text-background/80" />
                <span className="text-xs font-semibold text-background/90 uppercase tracking-wide">Mobile Access</span>
              </div>
              <div className="bg-background rounded-lg p-1 w-[min(100px,100vw)] md:w-[100px] h-[min(100px,100vh)] md:h-[100px] flex items-center justify-center">
                <LazyImage src={qrCodeUrl} alt="Scan to open Gloogum on mobile" width={92} height={92} className="rounded" />
              </div>
              <p className="text-[10px] text-background/75 mt-1.5 text-center">Scan to open on mobile</p>
            </div>
          </div>
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category} className={MOBILE_HIDDEN_CATEGORIES.includes(category) ? "hidden md:block" : undefined}>
              <h4 className="font-display font-semibold mb-4 text-sm uppercase tracking-wider text-background/85">{category}</h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link to={link.href} className="text-background/80 hover:text-background text-sm transition-colors">{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <Separator className="bg-background/10 mb-8" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-background/80 text-sm">
          <p>© 2026 Gloogum. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-background transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-background transition-colors">Privacy</Link>
            <Link to="/community-guidelines" className="hover:text-background transition-colors">Guidelines</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
